import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import InputField from "./InputField";
import SelectField from "./SelectField";

const TipoAccesorio = [
  "Accesorio",
  "Consumible",
  "Complemento",
  "Repuesto",
  "Módulo",
];

const AccesoriosModal = ({
  accesorioId = null,
  marcaId,
  modeloId,
  equipoId = null,
  onClose,
}) => {
  const [form, setForm] = useState({
    tipoAccesorio: "",
    descripcion: "",
    serial: "",
    aplicaSoloEquipo: false,
    fecha_instalacion: "",
  });

  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  // 🧩 Cargar accesorio
  useEffect(() => {
    const fetchAccesorio = async () => {
      try {
        const res = await axios.get(`${API}/accesorios/${accesorioId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setForm({
            tipoAccesorio: res.data.tipoAccesorio || "",
            descripcion: res.data.descripcion || "",
            serial: res.data.serial || "",
            aplicaSoloEquipo: !!res.data.equipo,
            fecha_instalacion: res.data.fecha_instalacion
              ? res.data.fecha_instalacion.slice(0, 10)
              : "",
          });
        }
      } catch (err) {
        toast.error("❌ No se pudo cargar el accesorio:", err);
      }
    };

    if (accesorioId) fetchAccesorio();
  }, [accesorioId, API, token]);

  // 🔑 Handler estándar (input, select, checkbox)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      return {
        ...prev,
        [name]: nextValue,
        ...(name === "aplicaSoloEquipo" && !checked
          ? { fecha_instalacion: "" }
          : {}),
      };
    });
  };

  const validate = () => {
    const required = ["tipoAccesorio", "descripcion", "serial"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Falta campo obligatorio: " + k);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const payload = {
        marcaId,
        modeloId,
        tipoAccesorio: form.tipoAccesorio,
        descripcion: form.descripcion,
        serial: form.serial,
        soloParaEquipo: form.aplicaSoloEquipo,
      };

      if (form.aplicaSoloEquipo) {
        payload.equipoId = equipoId;
        payload.fecha_instalacion = form.fecha_instalacion || null;
      }

      if (accesorioId) {
        await axios.put(`${API}/accesorios/${accesorioId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Accesorio actualizado con éxito");
      } else {
        await axios.post(`${API}/accesorios`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Accesorio creado con éxito");
      }

      onClose();
    } catch (err) {
      toast.error("Error al guardar accesorio:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {accesorioId ? "Editar accesorio" : "Nuevo accesorio"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Scroll interno */}
        <div className="flex-1 overflow-y-auto">
          <Card className="border-none shadow-none">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <SelectField
                  label="Tipo *"
                  name="tipoAccesorio"
                  value={form.tipoAccesorio}
                  options={TipoAccesorio.map((t) => ({
                    _id: t,
                    name: t,
                  }))}
                  onChange={handleChange}
                />

                <InputField
                  label="Descripción *"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  placeholder="Ingrese la descripción"
                />

                <InputField
                  label="Serial *"
                  name="serial"
                  value={form.serial}
                  onChange={handleChange}
                  placeholder="Ingrese el serial"
                />

                {/* Checkbox */}
                <div className="flex items-center gap-2 border-t pt-3">
                  <input
                    type="checkbox"
                    name="aplicaSoloEquipo"
                    id="aplicaSoloEquipo"
                    checked={form.aplicaSoloEquipo}
                    onChange={handleChange}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <label
                    htmlFor="aplicaSoloEquipo"
                    className="text-sm text-gray-700 select-none"
                  >
                    Aplicar solo para este equipo
                  </label>
                </div>

                {form.aplicaSoloEquipo && (
                  <InputField
                    label="Fecha de instalación"
                    name="fecha_instalacion"
                    type="date"
                    value={form.fecha_instalacion}
                    onChange={handleChange}
                  />
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    onClick={onClose}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={loading}
                  >
                    {loading ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AccesoriosModal;
